import React, { useState, useEffect } from 'react';

// Hooks
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { displayTooltip } from '~/hooks/displayTooltip';

//Utils
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION } from '~/utils/constants';
import { MESSAGES } from '~/utils/message';
import { getFullName, stringLength } from '~/utils/helperFunctions';

// Components
import OlcTablePagination from '~/components/General/OlcTablePagination';
import OlcBulkSelectionDropdown from '~/components/General/OlcBulkSelectionDropdown';

// MUI Components
import DataGrid from '~/components/General/OlcDataGrid';
import {
  Chip,
  Box,
  MenuItem,
  Typography,
  Tooltip,
  Stack,
  Menu,
  Checkbox,
  IconButton,
} from '@mui/material';

// Icons
import { NoContactCIcon } from '~/assets/images';
import DoneIcon from '@mui/icons-material/Done';
import RemoveIcon from '@mui/icons-material/Remove';

// Styles
import './styles.scss';
import TruncatedText from '~/components/General/TruncatedText';

const ContactsTable = ({
  pagination,
  updatePagination,
  setSelectedRows,
  selectedRows,
  searchApplied,
  totalContactsInDB,
  filterSize,
}) => {
  const [expandRow, setShowMoreRows] = useState([]);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [bulkSelect, setBulkSelect] = useState(false);
  const [removeBulkSelect, setRemoveBulkSelect] = useState(false);
  const [rows, setRows] = useState([]);
  const [showNoRows, setShowNoRows] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const pageSizes = DEFAULT_PAGE_SIZES;
  const open = Boolean(anchorEl);

  const navigate = useNavigate();

  const fetchedRows = useSelector((state) => state.contactsReducers.contacts);
  const rowsCount = useSelector((state) => state.contactsReducers.rowsCount);
  const to = useSelector((state) => state.contactsReducers.to);
  const from = useSelector((state) => state.contactsReducers.from);
  const lastPage = useSelector((state) => state.contactsReducers.lastPage || 1);
  const currentPage = useSelector((state) => state.contactsReducers.currentPage || 1);
  const perPage = useSelector(
    (state) => state.contactsReducers.perPage || DEFAULT_PAGINATION.pageSize
  );
  const isLoading = useSelector((state) => state.contactsReducers.loading);

  const classes = {
    root: 'tableGrid',
    main: 'tableGridMain',
    columnHeaders: 'tableGridHeaderWrapper',
    columnHeader: 'tableGridColumnHeader',
    columnHeaderTitle: 'tableGridColumnHeaderTitle',
    virtualScrollerContent: 'tableGridVirtualScrollerContent',
    virtualScrollerRenderZone: 'tableGridVirtualScrollerRenderZone',
    cell: 'tableGridCell',
    row: 'tableGridRow',
    columnHeaderTitleContainer: 'tableGridColumnHeaderTitleContainer',
  };

  const handleChangePage = (event, newPage) => {
    updatePagination({
      page: newPage,
      pageSize: pagination.pageSize,
    });
    setBulkSelect(false);
  };

  const handleRowClick = (params, event) => {
    if (event.ctrlKey || event.metaKey) {
      window.open(`/contacts/${params.id}`, '_blank');
    } else {
      navigate(`/contacts/${params.id}`);
    }
  };

  const handleRowHeight = (params) => {
    // Adjust the base row height and add additional height if the row is expanded
    const baseHeight = 50; // Set your base row height here
    const additionalHeight = expandRow.includes(params.id) ? 150 : 0; // Set the additional height when expanded

    return baseHeight + additionalHeight;
  };

  const selectAll = () => {
    setBulkSelect(true);
    const updatedRows = rows.map((contact) => ({ ...contact, checked: true }));
    setRows(updatedRows);
    setSelectedRows([-1]);
  };

  const selectVisble = () => {
    setBulkSelect(true);
    const updatedRows = rows.map((contact) => ({ ...contact, checked: true }));
    setRows(updatedRows);

    const selectedRowIds = updatedRows.map((contact) => contact.id);
    setSelectedRows(selectedRowIds);
  };

  const selectNone = () => {
    setBulkSelect(false);
    setRemoveBulkSelect(false);
    const updatedRows = rows.map((contact) => ({ ...contact, checked: false }));
    setRows(updatedRows);
    setSelectedRows([]);
  };

  const selectRow = (event, selectedRow) => {
    event.stopPropagation();

    // Update the selectedRow object directly in the rows array
    const updatedRows = rows.map((row) =>
      row.id === selectedRow.id ? { ...row, checked: !row.checked } : row
    );

    // If the user selects all contacts and then removes a single contact, update the selected rows array accordingly.
    if (selectedRows[0] === -1) {
      const filteredRows = rows.map((row) =>
        row.id === selectedRow.id ? { ...row, checked: !row.checked } : { ...row, checked: true }
      );

      const filteredRowsIds = filteredRows
        .map((contact) => contact.checked === true && contact.id)
        .filter(Boolean);

      setSelectedRows(filteredRowsIds);
      setRows(updatedRows);
      setRemoveBulkSelect(true);
      return;
    }

    setRows(updatedRows);

    const selectedRowIds = selectedRows.includes(selectedRow.id)
      ? selectedRows.filter((id) => id !== selectedRow.id) // Remove the ID if already selected
      : [...selectedRows, selectedRow.id]; // Add the ID if not selected

    // Update the selectedRows state with the new array
    setSelectedRows(selectedRowIds);

    setRemoveBulkSelect(true);
  };

  const bulkSelection = () => {
    const newBulkSelect = !bulkSelect;
    setBulkSelect(newBulkSelect);

    const updatedRows = rows.map((contact) => ({
      ...contact,
      checked: newBulkSelect,
    }));
    setRows(updatedRows);

    const selectedRowIds = updatedRows.map((contact) => contact.id);
    newBulkSelect ? setSelectedRows(selectedRowIds) : setSelectedRows([]);
  };

  const removeBulkSelection = () => {
    const newBulkSelect = !removeBulkSelect;
    setRemoveBulkSelect(newBulkSelect);
    setBulkSelect(newBulkSelect);

    const updatedRows = rows.map((contact) => ({
      ...contact,
      checked: newBulkSelect,
    }));
    setRows(updatedRows);
    setSelectedRows([]);
  };

  const isTooltip = displayTooltip();

  const columns = [
    {
      field: ' ',
      headerClassName: 'olcBulkSelectHeader',
      cellClassName: 'olcBulkSelectCell',
      renderHeader: () => (
        <Box className="checkboxHeader">
          {removeBulkSelect ? (
            <Checkbox
              className="bulkCheck newbulkCheck"
              icon={<Box className="bulkNotchecked" />}
              checkedIcon={<RemoveIcon />}
              checked={removeBulkSelect}
              onClick={removeBulkSelection}
            />
          ) : (
            <Checkbox
              className="bulkCheck newbulkCheck"
              icon={<Box className="bulkNotchecked newbulkNotchecked" />}
              checkedIcon={<DoneIcon />}
              checked={bulkSelect}
              onClick={bulkSelection}
            />
          )}
          <OlcBulkSelectionDropdown
            ariaLabel="Open contact selection options"
            onClick={(event) => setAnchorEl(event.currentTarget)}
            open={open}
          />
          <Menu
            anchorEl={anchorEl}
            id="account-menu"
            open={open}
            onClose={() => setAnchorEl(null)}
            onClick={() => setAnchorEl(null)}
            transformOrigin={{ horizontal: 'left', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
            classes={{
              paper: 'bulkMenuOptionsNew',
              list: 'bulkMenuOptionsList',
            }}
            sx={{
              marginTop: '10px',
            }}
          >
            <MenuItem
              onClick={rowsCount > 0 ? selectAll : undefined}
              className={selectedRows[0] === -1 ? 'highlight' : ''}
            >
              {MESSAGES.CONTACTS.SELECT_ALL_ITEMS} ({rowsCount})
            </MenuItem>
            <MenuItem onClick={selectNone}>{MESSAGES.CONTACTS.SELECT_NONE}</MenuItem>
            <MenuItem onClick={selectVisble}>{MESSAGES.CONTACTS.SELECT_VISIBLE_ITEMS}</MenuItem>
          </Menu>
        </Box>
      ),
      width: 75,
      sortable: false,
      renderCell: (params) => (
        <Box className="checkboxRow">
          <Checkbox
            onClick={(event) => selectRow(event, params.row)}
            className="bulkCheck newbulkCheck"
            icon={<Box className="bulkNotchecked" />}
            checkedIcon={<DoneIcon />}
            checked={params.row.checked}
          />
        </Box>
      ),
    },
    {
      field: 'firstName',
      headerName: 'Full Name',
      headerClassName: 'olcBulkSelectAdjacentHeader',
      width: 250,
      compactWidth: 218,
      sortable: false,
      flex: window.innerWidth >= 1550 ? 1 : 0,
      renderCell: (params) =>
        stringLength(
          `${params?.row?.firstName || ''}` + `${params?.row?.lastName || ''}`,
          params.row.dncMarkedAt ? 16 : 20
        ) ? (
          <Tooltip
            title={getFullName(
              (params?.row?.firstName || '') + ' ' + (params?.row?.lastName || '')
            )}
          >
            <Box className="fullNameWrapper">
              <Typography>
                {getFullName((params?.row?.firstName || '') + ' ' + (params?.row?.lastName || ''))}
              </Typography>
              {params.row.isReturnAddress && (
                <Chip label="Return" color="primary" className="returnAddressChip" />
              )}
              {params.row.dncMarkedAt && (
                <Tooltip title="Do Not Contact">
                  <Chip label="DNC" color="primary" className="returnAddressChip" />
                </Tooltip>
              )}
            </Box>
          </Tooltip>
        ) : (
          <Box className="fullNameWrapper" display="flex" alignItems="center">
            <Typography>
              {getFullName((params?.row?.firstName || '') + ' ' + (params?.row?.lastName || ''))}
            </Typography>
            {params.row.isReturnAddress && (
              <Chip label="Return" color="primary" className="returnAddressChip" />
            )}
            {params.row.dncMarkedAt && (
              <Tooltip title="Do Not Contact">
                <Chip label="DNC" color="primary" className="returnAddressChip" />
              </Tooltip>
            )}
          </Box>
        ),
    },
    {
      field: 'companyName',
      headerName: 'Company',
      width: 190,
      compactWidth: 170,
      sortable: false,
      flex: window.innerWidth >= 1400 ? 1 : 0,
      renderCell: (params) =>
        stringLength(`${params.row.companyName}`) ? (
          <Tooltip title={params.row.companyName}>
            <Box className="valueWrapper">
              <Typography>{params.row.companyName || ' -'}</Typography>
            </Box>
          </Tooltip>
        ) : (
          <Box className="valueWrapper">
            <Typography>{params.row.companyName || ' -'}</Typography>
          </Box>
        ),
    },
    {
      field: 'address1',
      headerName: 'Mailing Address',
      type: 'number',
      width: 240,
      sortable: false,
      flex: window.innerWidth >= 1400 ? 1 : 0,
      renderCell: (params) => (
        // stringLength(
        //   `${params.row.address1}` +
        //     `${params.row.address2}` +
        //     `${params.row.city}` +
        //     `${params.row.state}` +
        //     `${params.row.zip}`,
        //   35
        // ) ? (
        //   <Tooltip
        //     title={
        //       <div>
        //         {params.row.address1 + ' ' + params.row.address2}
        //         <br />
        //         {params.row.city + ', ' + params.row.state + ' ' + params.row.zip}
        //       </div>
        //     }
        //     disableHoverListener={isTooltip}
        //   >
        //     <Box className="addressWrapper">
        //       <Typography>
        //         {(params?.row?.address1 || '') + ' ' + (params?.row?.address2 || '')}
        //       </Typography>
        //       <Typography>
        //         {params.row.city + ', ' + params.row.state + ' ' + params.row.zip}
        //       </Typography>
        //     </Box>
        //   </Tooltip>
        // ) : stringLength(
        //     `${params.row.address1}` +
        //       `${params.row.address2}` +
        //       `${params.row.city}` +
        //       `${params.row.state}` +
        //       `${params.row.zip}`,
        //     0
        //   ) ? (
        //   <Box className="addressWrapper">
        //     <Typography>
        //       {(params?.row?.address1 || '') + ' ' + (params?.row?.address2 || '')}
        //     </Typography>
        //     <Typography>
        //       {params?.row?.city + ', ' + params?.row?.state + ' ' + params?.row?.zip}
        //     </Typography>
        //   </Box>
        // ) : (
        //   <>-</>
        // ),

        <TruncatedText
          htmlElement={
            <Box className="addressWrapper">
              <Typography
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: '100%',
                }}
              >
                {(params?.row?.address1 || '') + ' ' + (params?.row?.address2 || '')}
              </Typography>
              <Typography
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: '100%',
                }}
              >
                {(params?.row?.city || '') +
                  ', ' +
                  (params?.row?.state || '') +
                  ' ' +
                  (params?.row?.zip || '')}
              </Typography>
            </Box>
          }
          text={
            `${params?.row?.address1} ${params?.row?.address2}` +
            `${params?.row?.city} ` +
            `${params?.row?.state} ` +
            `${params?.row?.zip} `
          }
          maxLength={30}
        />
      ),
    },
    {
      field: 'addressStatus',
      headerName: 'Address Status',
      width: 220,
      sortable: false,
      flex: window.innerWidth >= 1400 ? 1 : 0,
      renderCell: (params) => (
        <Box className="valueWrapper">
          <Typography>{params?.row?.addressStatus || ' -'}</Typography>
        </Box>
      ),
    },
    {
      field: 'tags',
      headerName: 'Tags',
      width: 260,
      height: 500,
      sortable: false,
      flex: window.innerWidth >= 1400 ? 1 : 0,
      renderCell: (params) => (
        <Box className="tagsWrapper">
          {params.value.length
            ? params.value
                .slice(
                  0,
                  screenWidth < 1600
                    ? 2
                    : expandRow.includes(params.row.id)
                      ? params.value.length
                      : 2
                )
                .map((tag, index) => (
                  <Tooltip title={tag} key={index}>
                    <Chip
                      className={
                        expandRow.includes(params.row.id) || params.value.length < 3
                          ? 'singleTag'
                          : 'tagChips'
                      }
                      sx={{
                        maxWidth: '100px !important',
                      }}
                      key={index}
                      label={
                        !expandRow.includes(params.row.id) && tag.length > 25
                          ? tag.slice(0, 25) + `...`
                          : tag
                      }
                      color="primary"
                    />
                  </Tooltip>
                ))
            : ' -'}
          {params.value.length > 2 && (
            <Tooltip
              title={
                <Box className="tagsWrapper">
                  {params.value.slice(2).map((hiddenTag, index) => (
                    <Tooltip title={hiddenTag} key={index} placement="bottom-end">
                      <Chip
                        className="tagChips maxWidth"
                        sx={{
                          maxWidth: '100px !important',
                        }}
                        label={hiddenTag.length > 25 ? hiddenTag.slice(0, 25) + `...` : hiddenTag}
                        color="primary"
                      />
                    </Tooltip>
                  ))}
                </Box>
              }
            >
              <Chip
                className="tagChips maxWidth"
                label={`+ ${params.value.length - 2}`}
                color="primary"
              />
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Created Date',
      width: 200,
      sortable: false,
      flex: window.innerWidth >= 1400 ? 1 : 0,
      renderCell: (params) => (
        <Box className="valueWrapper">
          <Typography>{params?.row?.createdAt || ' -'}</Typography>
        </Box>
      ),
    },
  ];

  useEffect(() => {
    setSelectedRows([]);
  }, [currentPage, perPage]);

  useEffect(() => {
    !selectedRows.length && setRemoveBulkSelect(false);
    (rows.length && selectedRows.length === rows.length) || selectedRows[0] === -1
      ? (setBulkSelect(true), setRemoveBulkSelect(false))
      : setBulkSelect(false);
  }, [selectedRows, rows]);

  useEffect(() => {
    setRows(fetchedRows);
  }, [fetchedRows]);

  // Add delay for showing no rows overlay
  useEffect(() => {
    let timeoutId;

    if (!isLoading && rows.length === 0) {
      // Show "No Results Found" after a delay when not loading and no data
      timeoutId = setTimeout(() => {
        setShowNoRows(true);
      }, 300); // 300ms delay
    } else {
      // Hide immediately when loading or when we have data
      setShowNoRows(false);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isLoading, rows.length]);

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (rows.length === 0 && currentPage > 1) {
      handleChangePage('', currentPage - 1);
    }
  }, [rows]);

  return (
    <div
      style={{
        width: '100%',
      }}
      className={`table olcPrimaryListContent olcPrimaryListTable ${
        rows.length === 0 ? 'noResultsTable' : ''
      }`}
    >
      <DataGrid
        rows={rows}
        columns={columns}
        shrinkColumnsToFit
        loading={isLoading}
        pageSizeOptions={[20, 50, 100]}
        rowCount={rowsCount}
        pagination={pagination}
        paginationMode="server"
        onRowClick={handleRowClick}
        hideFooterSelectedRowCount
        hideFooterPagination
        rowSelection={false}
        columnHeaderHeight={47}
        getRowId={(row) => row?.id}
        rowHeight={55}
        disableColumnMenu={true}
        classes={{
          ...classes,
          virtualScroller: `tableGridVirtualScroller ${rows.length === 0 ? 'noResults' : ''}`,
        }}
        slots={{
          noRowsOverlay: () =>
            showNoRows ? <Typography className="noResultsText">No Results Found</Typography> : null,
          noResultsOverlay: () =>
            showNoRows ? <Typography className="noResultsText">No Results Found</Typography> : null,
        }}
      />
      {(isLoading || rowsCount > 0) && (
        <OlcTablePagination
          count={lastPage}
          page={currentPage}
          pageSize={perPage}
          pageSizes={pageSizes}
          onPageChange={handleChangePage}
          onPageSizeChange={(event) => {
            updatePagination({
              page: 1,
              pageSize: event.target.value,
            });
            setBulkSelect(false);
          }}
          from={from}
          to={to}
          total={rowsCount}
          loading={isLoading}
        />
      )}
    </div>
  );
};

export default ContactsTable;
